import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Instagram, Globe, Play, X } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import Reveal from '../components/Reveal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const mediaUrl = (url) => (url && url.startsWith('/api') ? `${API_URL}${url}` : url);

/**
 * YouTube-ID aus allen gängigen Link-Formaten lesen.
 * Die alte Website kannte /shorts/ nicht – dort blieben die Videos schwarz.
 */
function youTubeId(url) {
  if (!url) return null;
  const patterns = [
    /youtu\.be\/([A-Za-z0-9_-]{11})/,
    /[?&]v=([A-Za-z0-9_-]{11})/,
    /\/shorts\/([A-Za-z0-9_-]{11})/,
    /\/embed\/([A-Za-z0-9_-]{11})/,
    /\/live\/([A-Za-z0-9_-]{11})/,
  ];
  for (const re of patterns) {
    const m = url.match(re);
    if (m) return m[1];
  }
  return null;
}

function vimeoId(url) {
  const m = url?.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  return m ? m[1] : null;
}

/** Hochformat-Video (YouTube Shorts) erkennen, damit das Fenster passt */
function isVerticalVideo(url) {
  return !!url && /\/shorts\//.test(url);
}

function getVideoEmbed(url) {
  if (!url) return null;
  const yt = youTubeId(url);
  if (yt) return `https://www.youtube-nocookie.com/embed/${yt}?autoplay=1&rel=0&playsinline=1`;
  const vm = vimeoId(url);
  if (vm) return `https://player.vimeo.com/video/${vm}?autoplay=1`;
  return null;
}

function getVideoThumbnail(item) {
  if (item.poster_url || item.thumbnail_url) {
    return mediaUrl(item.poster_url || item.thumbnail_url);
  }
  const url = item.url || '';
  const yt = youTubeId(url);
  if (yt) {
    // Shorts: "oar2" liefert das Originalformat (hochkant), maxresdefault
    // wäre immer 16:9 mit dem Reel klein in der Mitte.
    return isVerticalVideo(url)
      ? `https://i.ytimg.com/vi/${yt}/oar2.jpg`
      : `https://img.youtube.com/vi/${yt}/maxresdefault.jpg`;
  }
  const vm = vimeoId(url);
  if (vm) return `https://vumbnail.com/${vm}.jpg`;
  return 'https://placehold.co/800x450/202020/6F6A63?text=Video';
}

/** Fallback-Kette, falls YouTube ein Format nicht ausliefert */
function handleThumbError(e) {
  const src = e.target.src;
  if (src.includes('/oar2.jpg')) {
    e.target.src = src.replace('/oar2.jpg', '/maxresdefault.jpg');
  } else if (src.includes('maxresdefault')) {
    e.target.src = src.replace('maxresdefault', 'hqdefault');
  }
}

/**
 * Portal-Detailseite nach CI (Kapitel 09B):
 * schmale Infospalte links (31), Referenzen rechts daneben (69).
 * Einspaltig erst unter 640 px.
 */
export default function PortalPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [portal, setPortal] = useState(null);
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');
  const [activeType, setActiveType] = useState('all');

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const portalRes = await fetch(`${API_URL}/api/portals/${slug}`);
      if (!portalRes.ok) {
        setNotFound(true);
        return;
      }
      const portalData = await portalRes.json();
      setPortal(portalData);

      const mediaRes = await fetch(`${API_URL}/api/media?portal=${portalData.id}`);
      if (mediaRes.ok) {
        setMedia(await mediaRes.json());
      }
    } catch (error) {
      console.error('Error fetching portal:', error);
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-foreground/20 border-t-brand rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground text-sm uppercase tracking-wider">Lädt</p>
        </div>
      </div>
    );
  }

  if (notFound || !portal) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="pt-40 pb-32 text-center container-custom">
          <h1 className="font-display text-4xl mb-6">Portfolio nicht gefunden</h1>
          <Link to="/portfolio" className="btn-primary">Zurück zur Übersicht</Link>
        </div>
        <Footer />
      </div>
    );
  }

  // Medienart (Foto / Video) zählen
  const typeCounts = {
    image: media.filter((m) => m.type !== 'video').length,
    video: media.filter((m) => m.type === 'video').length,
  };

  // Kategorien innerhalb der gewählten Medienart ableiten
  const mediaOfType =
    activeType === 'all'
      ? media
      : media.filter((m) => (activeType === 'video' ? m.type === 'video' : m.type !== 'video'));

  const categoryCounts = {};
  mediaOfType.forEach((item) => {
    (item.categories || []).forEach((c) => {
      const key = String(c).toLowerCase();
      categoryCounts[key] = (categoryCounts[key] || 0) + 1;
    });
  });
  const categories = Object.keys(categoryCounts).sort(
    (a, b) => categoryCounts[b] - categoryCounts[a]
  );

  const labelFor = (slug) =>
    slug
      .split('-')
      .map((w) => (w === 'bts' ? 'BTS' : w.charAt(0).toUpperCase() + w.slice(1)))
      .join(' ');

  const visibleMedia =
    activeCategory === 'all'
      ? mediaOfType
      : mediaOfType.filter((item) =>
          (item.categories || []).some((c) => String(c).toLowerCase() === activeCategory)
        );

  // Masonry-Spalten (Referenzfläche)
  const columnCount = 3;
  const columns = Array.from({ length: columnCount }, () => []);
  visibleMedia.forEach((item, index) => {
    columns[index % columnCount].push(item);
  });

  return (
    <div className="min-h-screen bg-background">
      <Navigation />

      <section className="pt-28 md:pt-32 pb-20">
        <div className="container-custom">
          <Reveal>
            <button
              onClick={() => navigate('/portfolio')}
              className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.15em] text-muted-foreground hover:text-brand transition-colors mb-10"
            >
              <ArrowLeft className="w-4 h-4" />
              Zurück
            </button>
          </Reveal>

          {/* CI: 31/69 – Beschreibung links, Referenzen rechts daneben (bis 640 px) */}
          <div className="grid grid-cols-1 min-[640px]:grid-cols-[31fr_69fr] gap-10 lg:gap-16 items-start">
            {/* Infospalte: bleibt beim Scrollen sichtbar */}
            <div className="min-[640px]:sticky min-[640px]:top-28 min-[640px]:self-start">
              <Reveal>
                {portal.image_url ? (
                  <img
                    src={mediaUrl(portal.image_url)}
                    alt={portal.name}
                    className="w-36 h-36 md:w-44 md:h-44 rounded-full object-cover mb-8 border-4 border-card shadow-lg"
                  />
                ) : (
                  <div className="w-36 h-36 md:w-44 md:h-44 rounded-full bg-brand flex items-center justify-center mb-8">
                    <img src="/logo-mark-white.png" alt="" className="w-20 h-auto" />
                  </div>
                )}
              </Reveal>

              <Reveal delay={0.1}>
                <h1 className="font-display text-4xl md:text-5xl text-foreground mb-2">
                  {portal.name}
                </h1>
              </Reveal>

              {portal.tagline && (
                <Reveal delay={0.15}>
                  <p className="font-accent text-brand text-2xl mb-5">{portal.tagline}</p>
                </Reveal>
              )}

              <Reveal delay={0.2}>
                <div className="flex items-center gap-3 mb-6">
                  {portal.instagram_url && (
                    <a
                      href={portal.instagram_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Instagram"
                      className="w-10 h-10 rounded-full border border-border flex items-center justify-center text-foreground/70 hover:bg-ink hover:text-white transition-colors"
                    >
                      <Instagram className="w-4 h-4" />
                    </a>
                  )}
                  {portal.website_url && (
                    <a
                      href={portal.website_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Website"
                      className="w-10 h-10 rounded-full border border-border flex items-center justify-center text-foreground/70 hover:bg-ink hover:text-white transition-colors"
                    >
                      <Globe className="w-4 h-4" />
                    </a>
                  )}
                </div>
              </Reveal>

              {portal.bio && (
                <Reveal delay={0.25}>
                  <p className="text-foreground/75 leading-relaxed text-justify hyphens-auto" lang="de">
                    {portal.bio}
                  </p>
                </Reveal>
              )}

              {(portal.email || portal.phone) && (
                <Reveal delay={0.3}>
                  <div className="mt-8 pt-6 border-t border-border text-sm text-muted-foreground space-y-1">
                    {portal.email && (
                      <p><a href={`mailto:${portal.email}`} className="hover:text-brand transition-colors">{portal.email}</a></p>
                    )}
                    {portal.phone && (
                      <p><a href={`tel:${portal.phone.replace(/\s/g, '')}`} className="hover:text-brand transition-colors">{portal.phone}</a></p>
                    )}
                  </div>
                </Reveal>
              )}
            </div>

            {/* Referenzfläche: Selected Work + Medienraster */}
            <div>
              <Reveal>
                <span className="kicker mb-6 inline-flex">Selected Work</span>
              </Reveal>

              {/* Medienart: Foto / Video */}
              {typeCounts.image > 0 && typeCounts.video > 0 && (
                <Reveal delay={0.03}>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {[
                      { key: 'all', label: 'Alles', count: media.length },
                      { key: 'image', label: 'Foto', count: typeCounts.image },
                      { key: 'video', label: 'Video', count: typeCounts.video },
                    ].map((t) => (
                      <button
                        key={t.key}
                        onClick={() => { setActiveType(t.key); setActiveCategory('all'); }}
                        className={`px-4 py-2 text-xs uppercase tracking-[0.15em] border transition-colors ${
                          activeType === t.key
                            ? 'bg-ink text-white border-ink'
                            : 'bg-transparent text-foreground border-border hover:border-foreground'
                        }`}
                      >
                        {t.label} <span className="opacity-60">({t.count})</span>
                      </button>
                    ))}
                  </div>
                </Reveal>
              )}

              {/* Kategorie-Filter */}
              {categories.length > 1 && (
                <Reveal delay={0.05}>
                  <div className="flex flex-wrap gap-x-1 gap-y-2 mb-8 border-b border-border">
                    <button
                      onClick={() => setActiveCategory('all')}
                      className={`filter-tab ${activeCategory === 'all' ? 'active' : ''}`}
                    >
                      Alle <span className="opacity-50">({mediaOfType.length})</span>
                    </button>
                    {categories.map((cat) => (
                      <button
                        key={cat}
                        onClick={() => setActiveCategory(cat)}
                        className={`filter-tab ${activeCategory === cat ? 'active' : ''}`}
                      >
                        {labelFor(cat)} <span className="opacity-50">({categoryCounts[cat]})</span>
                      </button>
                    ))}
                  </div>
                </Reveal>
              )}

              <div className="grid grid-cols-2 lg:grid-cols-3 gap-2 md:gap-3">
                {columns.map((column, colIndex) => (
                  <div key={colIndex} className="flex flex-col gap-2 md:gap-3">
                    {column.map((item, itemIndex) => (
                      <Reveal key={item.id} delay={Math.min(itemIndex * 0.06, 0.4)} y={32} blur={6}>
                        <div
                          className="relative overflow-hidden bg-muted group cursor-pointer"
                          onClick={() => item.type === 'video' && setSelectedVideo(item)}
                        >
                          {item.type === 'video' ? (
                            <div className="relative">
                              <img
                                src={getVideoThumbnail(item)}
                                alt={item.title}
                                loading="lazy"
                                decoding="async"
                                className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                                onError={handleThumbError}
                              />
                              <div className="absolute inset-0 flex items-center justify-center">
                                <div className="w-14 h-14 rounded-full bg-white shadow-lg flex items-center justify-center group-hover:scale-110 group-hover:bg-yellow transition-all duration-300">
                                  <Play className="w-6 h-6 text-ink fill-current ml-0.5" />
                                </div>
                              </div>
                            </div>
                          ) : (
                            <img
                              src={mediaUrl(item.url)}
                              alt={item.title}
                              loading="lazy"
                              decoding="async"
                              className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                            />
                          )}
                        </div>
                      </Reveal>
                    ))}
                  </div>
                ))}
              </div>

              {visibleMedia.length === 0 && (
                <p className="text-muted-foreground py-16 text-center border border-dashed border-border">
                  {media.length === 0
                    ? 'Diesem Portfolio sind noch keine Arbeiten zugeordnet.'
                    : 'In dieser Kategorie sind noch keine Arbeiten vorhanden.'}
                </p>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Video Modal */}
      <AnimatePresence>
        {selectedVideo && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
            onClick={() => setSelectedVideo(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className={`relative bg-black overflow-hidden ${isVerticalVideo(selectedVideo.url) ? 'h-[85vh] aspect-[9/16]' : 'w-full max-w-5xl aspect-video'}`}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setSelectedVideo(null)}
                className="absolute top-4 right-4 z-10 w-10 h-10 bg-white/20 hover:bg-yellow hover:text-ink rounded-full flex items-center justify-center transition-colors text-white"
                aria-label="Video schließen"
              >
                <X className="w-5 h-5" />
              </button>
              {getVideoEmbed(selectedVideo.url) ? (
                <iframe
                  src={getVideoEmbed(selectedVideo.url)}
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title={selectedVideo.title}
                />
              ) : (
                <video
                  src={mediaUrl(selectedVideo.url)}
                  className="w-full h-full object-contain"
                  controls
                  autoPlay
                  playsInline
                />
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <Footer />
    </div>
  );
}
